import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Settings, Search, Pin, PinOff, BellOff, Bell, LogOut, Hash, MessageCircle } from 'lucide-react';
import FavoriteToggle from '../ui/FavoriteToggle';
import toast from 'react-hot-toast';
import { updateChannel, leaveChannel, type ChatChannelDetail } from '../../services/chatChannelService';
import ChannelSettingsModal from './ChannelSettingsModal';
import { useQueryClient } from '@tanstack/react-query';

interface Props {
  channel: ChatChannelDetail;
  onShowMembers: () => void;
  onToggleSearch: () => void;
  pinnedCount?: number;
  onTogglePinnedList?: () => void;
}

export default function ChatChannelHeader({ channel, onShowMembers, onToggleSearch, pinnedCount, onTogglePinnedList }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    if (menuOpen) document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [menuOpen]);

  const displayName = channel.type === 'PROJECT'
    ? channel.project?.name ?? channel.name
    : channel.type === 'USER'
      ? channel.name
      : channel.members.filter((m) => m.userId !== channel.createdBy).map((m) => m.name).join(', ') || 'DM';

  const handleTogglePin = async () => {
    await updateChannel(channel.id, { isPinned: !channel.isPinned });
    queryClient.invalidateQueries({ queryKey: ['chatChannel', channel.id] });
    queryClient.invalidateQueries({ queryKey: ['chatChannels'] });
    toast.success(channel.isPinned ? '고정 해제됨' : '상단 고정됨');
    setMenuOpen(false);
  };

  const handleToggleMute = async () => {
    await updateChannel(channel.id, { isMuted: !channel.isMuted });
    queryClient.invalidateQueries({ queryKey: ['chatChannel', channel.id] });
    queryClient.invalidateQueries({ queryKey: ['chatChannels'] });
    toast.success(channel.isMuted ? '알림 켜짐' : '알림 꺼짐');
    setMenuOpen(false);
  };

  const handleLeave = async () => {
    try {
      await leaveChannel(channel.id);
      queryClient.invalidateQueries({ queryKey: ['chatChannels'] });
      toast.success('채널에서 나갔습니다.');
      navigate('/chat');
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '채널 나가기에 실패했습니다.';
      toast.error(msg);
    }
    setMenuOpen(false);
  };

  return (
    <div className="h-14 border-b border-gray-200 bg-white flex items-center justify-between px-4 shrink-0">
      <div className="flex items-center gap-2 min-w-0">
        {channel.type === 'PROJECT' && channel.project && (
          <span className="text-[11px] bg-gray-100 text-gray-600 rounded px-1 py-0.5 font-mono shrink-0">
            {channel.project.projectKey}
          </span>
        )}
        {channel.type === 'USER' && <Hash className="size-4 text-gray-400 shrink-0" />}
        {channel.type === 'DM' && <MessageCircle className="size-4 text-gray-400 shrink-0" />}
        <span className="font-semibold text-gray-900 truncate">{displayName}</span>
        <FavoriteToggle targetType="CHANNEL" targetId={channel.id} />
        {channel.isArchived && (
          <span className="flex items-center gap-1 text-[11px] text-yellow-600 bg-yellow-100 rounded-full px-2 py-0.5 shrink-0">
            📦 아카이브됨
          </span>
        )}
        <span className="text-sm text-gray-500 shrink-0">{channel.memberCount}명</span>
      </div>

      <div className="flex items-center gap-1">
        <button onClick={onToggleSearch}
          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors">
          <Search className="size-4" />
        </button>
        {onTogglePinnedList && (
          <button onClick={onTogglePinnedList}
            className="flex items-center gap-0.5 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors">
            <Pin className="size-4" />
            {(pinnedCount ?? 0) > 0 && (
              <span className="text-[11px] bg-yellow-100 text-yellow-800 rounded-full px-1 min-w-[16px] text-center font-medium">
                {pinnedCount}
              </span>
            )}
          </button>
        )}
        <button onClick={onShowMembers}
          className="flex items-center gap-1 px-2 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors">
          <Users className="size-4" />
          <span className="text-sm">{channel.memberCount}</span>
        </button>

        <div ref={menuRef} className="relative">
          <button onClick={() => setMenuOpen((v) => !v)}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors">
            <Settings className="size-4" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-20">
              <button onClick={handleTogglePin}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                {channel.isPinned ? <PinOff className="size-4" /> : <Pin className="size-4" />}
                {channel.isPinned ? '고정 해제' : '상단 고정'}
              </button>
              <button onClick={handleToggleMute}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                {channel.isMuted ? <Bell className="size-4" /> : <BellOff className="size-4" />}
                {channel.isMuted ? '알림 켜기' : '알림 끄기'}
              </button>
              {channel.type === 'USER' && (
                <button onClick={() => { setShowSettings(true); setMenuOpen(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                  <Settings className="size-4" />
                  채널 설정
                </button>
              )}
              {channel.type !== 'PROJECT' && (
                <button onClick={handleLeave}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50">
                  <LogOut className="size-4" />
                  채널 나가기
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {showSettings && (
        <ChannelSettingsModal channel={channel} onClose={() => setShowSettings(false)} />
      )}
    </div>
  );
}
